import React, { useState, useEffect } from "react";
import BackNav from "./BackNav";
import { settingsService } from "../services/settingsService";
import { useUnsavedChanges } from "../services/unsavedChangesService";
import Header from "./Header";
import Loading from "./Loading";
import ConfirmationModal from "./ConfirmationModal";
import "./SettingsPage.css";

// Toast type enum for better management
enum ToastType {
  SUCCESS = "success",
  ERROR = "error",
}

interface SettingsPageProps {
  user?: {
    userid: string;
    email: string;
    name: string;
  };
  onLogout: () => void;
}

interface SettingsFormData {
  collaboflowApiKey: string;
  accessToken: string;
  subscriptionKey: string;
  tenantId: string;
  currentApiKey: string;
  batchUserId: string;
  appCd: number | string;
  settingId: number;
  apiKeyId: number;
}

interface FormErrors {
  collaboflowApiKey?: string;
  accessToken?: string;
  subscriptionKey?: string;
  tenantId?: string;
  batchUserId?: string;
  appCd?: number | string;
}

const SettingsPage: React.FC<SettingsPageProps> = ({ user, onLogout }) => {
  const [formData, setFormData] = useState<SettingsFormData>({
    collaboflowApiKey: "",
    accessToken: "",
    subscriptionKey: "",
    tenantId: "",
    currentApiKey: "",
    batchUserId: "",
    appCd: "",
    settingId: 0,
    apiKeyId: 0,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isApiKeyDisabled, setIsApiKeyDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState<ToastType>(ToastType.SUCCESS);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialFormData, setInitialFormData] =
    useState<SettingsFormData | null>(null);
  const [showDeleteAppCdDialog, setShowDeleteAppCdDialog] = useState(false);
  const [isAppCdEditable, setIsAppCdEditable] = useState(true);
  const [appCdDeleted, setAppCdDeleted] = useState(false);

  // Helper function to show toast messages
  const showToast = (message: string, type: ToastType = ToastType.SUCCESS) => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => setToastMessage(""), 3000);
  };

  // Check if form has unsaved changes
  const checkForUnsavedChanges = () => {
    if (!initialFormData) return false;

    // Skip warning if app_cd was just deleted
    if (appCdDeleted) return false;

    try {
      return (
        formData.collaboflowApiKey !== initialFormData.collaboflowApiKey ||
        formData.accessToken !== initialFormData.accessToken ||
        formData.subscriptionKey !== initialFormData.subscriptionKey ||
        formData.tenantId !== initialFormData.tenantId ||
        formData.batchUserId !== initialFormData.batchUserId ||
        formData.appCd !== initialFormData.appCd
      );
    } catch (error) {
      console.error("Error checking unsaved changes:", error);
      return false;
    }
  };

  // Use unsaved changes service
  const { handleLogoClick, handleLogoutClick } = useUnsavedChanges({
    hasUnsavedChanges,
    warningMessage: "変更が保存されていません。このページを離れますか？",
  });

  // Track form changes
  useEffect(() => {
    try {
      if (initialFormData) {
        const hasChanges = checkForUnsavedChanges();
        setHasUnsavedChanges(hasChanges);
      }
    } catch (error) {
      console.error("Error in form change tracking:", error);
    }
  }, [formData, initialFormData]);

  // Load settings data on component mount
  useEffect(() => {
    let isMounted = true;

    const loadSettings = async () => {
      if (!isMounted || isInitialized) return;

      setIsLoading(true);

      try {
        // First, get settings data
        const settingsResponse = await settingsService.getSettings();

        if (settingsResponse.success && settingsResponse.data && isMounted) {
          const settings = settingsResponse.data;

          // Set initial form data (including apiKeyId)
          const initialFormData = {
            collaboflowApiKey: settings.collaboApiKey || "",
            accessToken: settings.bugyoAccessToken || "",
            subscriptionKey: settings.bugyoSupscriptionKey || "",
            tenantId: settings.bugyoTenantId || "",
            currentApiKey: "",
            batchUserId: settings.collaboUserid || "", // Use collaboUserid from backend
            appCd: settings.appCd || "", // Use appCd from backend
            settingId: settings.id,
            apiKeyId: settings.apiKeyId || 0,
          };

          setFormData(prev => ({ ...prev, ...initialFormData }));
          setInitialFormData(initialFormData);

          // Set app_cd editable state based on whether it has a value
          setIsAppCdEditable(!settings.appCd);

          // Don't validate on initial load - only validate when user interacts
          // This prevents showing errors for null values from backend

          // Load API key immediately if we have an apiKeyId
          if (settings.apiKeyId && isMounted) {
            try {
              const apiKeyResponse = await settingsService.getApiKey(
                settings.apiKeyId
              );

              if (apiKeyResponse.success && apiKeyResponse.data && isMounted) {
                const apiKey = apiKeyResponse.data;
                setFormData(prev => ({
                  ...prev,
                  currentApiKey: apiKey.encryptedKey,
                  apiKeyId: apiKey.id,
                }));
                setIsApiKeyDisabled(apiKey.isDisabled);
              } else {
                console.log(
                  "API key response failed or no data:",
                  apiKeyResponse
                );
              }
            } catch (apiKeyError) {
              console.error("Failed to load immediate API key:", apiKeyError);
              if (isMounted) {
                showToast("APIキーの読み込みに失敗しました。", ToastType.ERROR);
              }
            }
          }
        }
      } catch (error) {
        console.error("Failed to load settings:", error);
        if (isMounted) {
          showToast("設定の読み込みに失敗しました。", ToastType.ERROR);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
          setIsInitialized(true);
        }
      }
    };

    loadSettings();

    return () => {
      isMounted = false;
    };
  }, [isInitialized]);

  // Validation functions based on CSV documentation
  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case "collaboflowApiKey":
        if (!value.trim()) return "APIキーを入力してください。";
        if (value.length > 500)
          return "APIキーは500文字以下で入力してください。";
        // Allow alphanumeric and special characters
        if (!/^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]+$/.test(value))
          return "APIキーは英数字と特殊文字で入力してください。";
        break;
      case "accessToken":
        if (!value.trim()) return "アクセストークンを入力してください。";
        if (value.length > 1000)
          return "アクセストークンは1000文字以下で入力してください。";
        // Alphanumeric and special characters
        if (!/^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]+$/.test(value))
          return "アクセストークンは英数字と特殊文字で入力してください。";
        break;
      case "subscriptionKey":
        if (!value.trim()) return "サブスクリプションキーを入力してください。";
        if (value.length > 1000)
          return "サブスクリプションキーは1000文字以下で入力してください。";
        break;
      case "tenantId":
        if (!value.trim()) return "テナントIDを入力してください。";
        if (value.length > 1000)
          return "テナントIDは1000文字以下で入力してください。";
        break;
      case "batchUserId":
        if (!value.trim()) return "ユーザーIDを入力してください。";
        if (value.length < 2) return "2文字以上の文字列である必要があります。";
        if (value.length > 100)
          return "ユーザーIDは100文字以下で入力してください。";
        break;
      case "appCd":
        if (!value.trim())
          return "アプリケーションのコードを入力してください。";
        // Check if it's a valid number
        if (isNaN(Number(value))) return "app_cdは数値で入力してください。";
        // Check if it's a positive integer
        const numValue = Number(value);
        if (!Number.isInteger(numValue) || numValue <= 0)
          return "app_cdは正の整数で入力してください。";
        // Check length (as string)
        if (value.length > 10) return "app_cdは10桁以下で入力してください。";
        break;
    }
    return undefined;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Filter invalid characters based on field type
    let filteredValue = value;
    if (name === "collaboflowApiKey") {
      // Allow alphanumeric and special characters
      filteredValue = value.replace(
        /[^a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/g,
        ""
      );
    } else if (name === "accessToken") {
      // Only allow alphanumeric and special characters
      filteredValue = value.replace(
        /[^a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/g,
        ""
      );
    } else if (name === "appCd") {
      // Only allow digits
      filteredValue = value.replace(/[^0-9]/g, "");
    }

    setFormData(prev => ({ ...prev, [name]: filteredValue }));

    // Reset appCdDeleted flag when user starts editing
    if (appCdDeleted) {
      setAppCdDeleted(false);
    }

    // Mark that user has interacted with the form
    if (!hasUserInteracted) {
      setHasUserInteracted(true);
    }

    // Clear field error when user starts typing (like login form)
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }

    // Check for unsaved changes
    const hasChanges = checkForUnsavedChanges();
    setHasUnsavedChanges(hasChanges);
  };

  const handleBlur = (fieldName: string) => {
    // Mark that user has interacted with the form
    if (!hasUserInteracted) {
      setHasUserInteracted(true);
    }

    // Mark this field as touched
    setTouchedFields(prev => new Set(prev).add(fieldName));

    // Validate only the specific field on blur (like login form)
    const value = formData[fieldName as keyof SettingsFormData];
    const error = validateField(
      fieldName,
      typeof value === "string" ? value : String(value)
    );

    // Only update error for this specific field, don't touch other fields
    setErrors(prev => ({
      ...prev,
      [fieldName]: error,
    }));
  };

  const handleCopyApiKey = async () => {
    try {
      await navigator.clipboard.writeText(formData.currentApiKey);
      showToast("クリップボードにコピーしました。", ToastType.SUCCESS);
    } catch (error) {
      console.error("Failed to copy API key:", error);
      showToast("コピーに失敗しました。", ToastType.ERROR);
    }
  };

  const handleGenerateNewApiKey = async () => {
    setIsLoading(true);
    try {
      const response = await settingsService.generateNewApiKey(
        formData.settingId
      );
      if (response.success && response.data) {
        setFormData(prev => ({
          ...prev,
          currentApiKey: response.data!.encryptedKey,
          apiKeyId: response.data!.id,
        }));
        setIsApiKeyDisabled(response.data!.isDisabled);
        showToast("作成しました。", ToastType.SUCCESS);
      } else {
        showToast("作成に失敗しました。", ToastType.ERROR);
        console.error("API key generation failed:", response.message);
      }
    } catch (error) {
      console.error("Failed to generate API key:", error);
      showToast("作成に失敗しました。", ToastType.ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleApiKey = async () => {
    setIsLoading(true);
    try {
      const newDisabledState = !isApiKeyDisabled;
      const response = await settingsService.toggleApiKeyStatus(
        formData.apiKeyId,
        newDisabledState
      );
      if (response.success && response.data) {
        const apiKeyData = response.data;
        setIsApiKeyDisabled(apiKeyData.isDisabled);
        if (apiKeyData.encryptedKey) {
          setFormData(prev => ({
            ...prev,
            currentApiKey: apiKeyData.encryptedKey,
          }));
        }
        const message = apiKeyData.isDisabled
          ? "このAPIキーは既に無効化されています。"
          : "APIキーを有効化しました。";
        showToast(message, ToastType.SUCCESS);
      } else {
        showToast("操作に失敗しました。", ToastType.ERROR);
      }
    } catch (error) {
      console.error("Failed to toggle API key:", error);
      showToast("操作に失敗しました。", ToastType.ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAppCd = () => {
    setShowDeleteAppCdDialog(true);
  };

  const handleConfirmDeleteAppCd = async () => {
    setIsLoading(true);
    try {
      const response = await settingsService.deleteAppCd();
      if (response.success) {
        setFormData(prev => ({ ...prev, appCd: "" }));
        setIsAppCdEditable(true);
        setShowDeleteAppCdDialog(false);
        setAppCdDeleted(true); // Mark that app_cd was deleted
        showToast("app_cdを削除しました。", ToastType.SUCCESS);
      } else {
        showToast("削除に失敗しました。", ToastType.ERROR);
      }
    } catch (error) {
      console.error("Failed to delete app_cd:", error);
      showToast("削除に失敗しました。", ToastType.ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelDeleteAppCd = () => {
    setShowDeleteAppCdDialog(false);
  };

  const handleSave = async () => {
    // Mark that user has interacted when trying to save
    if (!hasUserInteracted) {
      setHasUserInteracted(true);
    }

    // Validate form but only show errors for fields that have been interacted with
    const newErrors: FormErrors = {};
    let isValid = true;

    Object.keys(formData).forEach(key => {
      if (
        key !== "currentApiKey" &&
        key !== "settingId" &&
        key !== "apiKeyId"
      ) {
        const value = formData[key as keyof SettingsFormData];
        if (typeof value === "string") {
          const error = validateField(key, value);
          if (error) {
            newErrors[key as keyof FormErrors] = error;
            isValid = false;
          }
        }
      }
    });

    setErrors(newErrors);

    if (!isValid) {
      return;
    }

    setIsLoading(true);
    try {
      const settingsData = {
        collaboApiKey: formData.collaboflowApiKey,
        bugyoAccessToken: formData.accessToken,
        bugyoSupscriptionKey: formData.subscriptionKey,
        bugyoTenantId: formData.tenantId,
        collaboUserid: formData.batchUserId,
        appCd: formData.appCd as string,
      };

      const response = await settingsService.saveSettings(settingsData);
      if (response.success) {
        showToast("保存しました。", ToastType.SUCCESS);
        // Reset unsaved changes flag and update initial data
        setHasUnsavedChanges(false);
        setInitialFormData({
          ...formData,
          collaboflowApiKey: formData.collaboflowApiKey,
          accessToken: formData.accessToken,
          subscriptionKey: formData.subscriptionKey,
          tenantId: formData.tenantId,
          batchUserId: formData.batchUserId,
          appCd: formData.appCd,
        });

        // Update app_cd editable state based on whether it has a value
        setIsAppCdEditable(!formData.appCd);
      } else {
        showToast("保存に失敗しました。", ToastType.ERROR);
      }
    } catch (error) {
      console.error("Failed to save settings:", error);
      showToast("保存に失敗しました。", ToastType.ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const isSaveButtonDisabled = () => {
    // Check if any touched field has validation errors
    const hasErrors = Array.from(touchedFields).some(
      fieldName => errors[fieldName as keyof FormErrors] !== undefined
    );

    // Check if any required field is empty
    const hasEmptyFields =
      !formData.collaboflowApiKey.trim() ||
      !formData.accessToken.trim() ||
      !formData.subscriptionKey.trim() ||
      !formData.tenantId.trim() ||
      !formData.batchUserId.trim() ||
      !String(formData.appCd).trim();

    return hasErrors || hasEmptyFields;
  };

  if (!isInitialized) {
    return (
      <div className="settings-page">
        <Loading message="設定を読み込み中..." fullHeight={true} />
      </div>
    );
  }

  return (
    <div className="settings-page">
      {/* Header */}
      <Header
        user={user}
        onLogout={onLogout}
        onLogoClick={handleLogoClick}
        onLogoutClick={() => handleLogoutClick(onLogout)}
      />
      {/* Back Button (aligned with content) */}
      <BackNav
        label="ダッシュボードに戻る"
        to="/"
        className="settings-page"
        hasUnsavedChanges={hasUnsavedChanges}
      />

      {/* Main Content */}
      <main className="settings-main">
        <div className="settings-container">
          <div className="settings-header-section">
            <h2 className="settings-title">管理者設定</h2>
            <p className="settings-description">
              システム連携に関する設定を行います
            </p>
          </div>

          <form className="settings-form">
            {/* CollaboFlow API Key Settings */}
            <div className="settings-section">
              <h3 className="section-title">コラボフロー認証情報</h3>
              <div className="form-group">
                <label htmlFor="collaboflowApiKey" className="form-label">
                  APIキー
                </label>
                <input
                  type="text"
                  id="collaboflowApiKey"
                  name="collaboflowApiKey"
                  value={formData.collaboflowApiKey}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("collaboflowApiKey")}
                  placeholder="APIキーを入力"
                  maxLength={500}
                  className={`form-input ${touchedFields.has("collaboflowApiKey") && errors.collaboflowApiKey ? "error" : ""}`}
                />
                {touchedFields.has("collaboflowApiKey") &&
                  errors.collaboflowApiKey && (
                    <span className="error-message">
                      {errors.collaboflowApiKey}
                    </span>
                  )}
              </div>
              <div className="form-group">
                <label htmlFor="appCd" className="form-label">
                  app_cd
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  id="appCd"
                  name="appCd"
                  value={formData.appCd}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("appCd")}
                  placeholder="アプリケーションのコードを入力"
                  maxLength={10}
                  disabled={!isAppCdEditable}
                  className={`form-input ${!isAppCdEditable ? "disabled" : ""} ${touchedFields.has("appCd") && errors.appCd ? "error" : ""}`}
                />
                {!isAppCdEditable && formData.appCd && (
                  <div className="api-key-buttons">
                    <button
                      type="button"
                      onClick={handleDeleteAppCd}
                      className="api-key-btn delete-btn"
                      title="app_cdを削除"
                    >
                      削除
                    </button>
                  </div>
                )}
                {touchedFields.has("appCd") && errors.appCd && (
                  <span className="error-message">{errors.appCd}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="batchUserId" className="form-label">
                  ユーザーID
                </label>
                <input
                  type="text"
                  id="batchUserId"
                  name="batchUserId"
                  value={formData.batchUserId}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("batchUserId")}
                  placeholder="ユーザーIDを入力"
                  maxLength={100}
                  className={`form-input ${touchedFields.has("batchUserId") && errors.batchUserId ? "error" : ""}`}
                />
                {touchedFields.has("batchUserId") && errors.batchUserId && (
                  <span className="error-message">{errors.batchUserId}</span>
                )}
              </div>
            </div>

            {/* Bugyo Cloud Connection Settings */}
            <div className="settings-section">
              <h3 className="section-title">商奉行 接続情報設定</h3>
              <div className="form-group">
                <label htmlFor="accessToken" className="form-label">
                  アクセストークン
                </label>
                <input
                  type="text"
                  id="accessToken"
                  name="accessToken"
                  value={formData.accessToken}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("accessToken")}
                  placeholder="アクセストークンを入力"
                  maxLength={1000}
                  className={`form-input ${touchedFields.has("accessToken") && errors.accessToken ? "error" : ""}`}
                />
                {touchedFields.has("accessToken") && errors.accessToken && (
                  <span className="error-message">{errors.accessToken}</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="subscriptionKey" className="form-label">
                  サブスクリプションキー
                </label>
                <input
                  type="text"
                  id="subscriptionKey"
                  name="subscriptionKey"
                  value={formData.subscriptionKey}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("subscriptionKey")}
                  placeholder="サブスクリプションキーを入力"
                  maxLength={1000}
                  className={`form-input ${touchedFields.has("subscriptionKey") && errors.subscriptionKey ? "error" : ""}`}
                />
                {touchedFields.has("subscriptionKey") &&
                  errors.subscriptionKey && (
                    <span className="error-message">
                      {errors.subscriptionKey}
                    </span>
                  )}
              </div>
              <div className="form-group">
                <label htmlFor="tenantId" className="form-label">
                  テナントID
                </label>
                <input
                  type="text"
                  id="tenantId"
                  name="tenantId"
                  value={formData.tenantId}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("tenantId")}
                  placeholder="テナントIDを入力"
                  maxLength={1000}
                  className={`form-input ${touchedFields.has("tenantId") && errors.tenantId ? "error" : ""}`}
                />
                {touchedFields.has("tenantId") && errors.tenantId && (
                  <span className="error-message">{errors.tenantId}</span>
                )}
              </div>
            </div>

            {/* Collabo for Bugyo Server API Key Issuance */}
            {!!(initialFormData?.collaboflowApiKey || initialFormData?.accessToken || initialFormData?.subscriptionKey || initialFormData?.tenantId) && <div className="settings-section">
              <h3 className="section-title">コラボフロー for 商奉行サーバーAPIキー発行</h3>
              <div className="form-group">
                <label htmlFor="currentApiKey" className="form-label">
                  現在のAPIキー
                </label>
                <div className="api-key-display">
                  <input
                    type="text"
                    id="currentApiKey"
                    value={formData.currentApiKey}
                    readOnly
                    className={`form-input readonly ${isApiKeyDisabled ? "disabled" : ""}`}
                  />
                  {isApiKeyDisabled && (
                    <span
                      className="disabled-icon"
                      title="このAPIキーは無効化されています。"
                    >
                      ⚠️
                    </span>
                  )}
                </div>
                <div className="api-key-buttons">
                  <button
                    type="button"
                    onClick={handleCopyApiKey}
                    disabled={isApiKeyDisabled}
                    className="api-key-btn copy-btn"
                  >
                    コピー
                  </button>
                  <button
                    type="button"
                    onClick={handleGenerateNewApiKey}
                    disabled={isLoading}
                    className="api-key-btn generate-btn"
                  >
                    新規発行
                  </button>
                  <button
                    type="button"
                    onClick={handleToggleApiKey}
                    disabled={isLoading}
                    className="api-key-btn toggle-btn"
                  >
                    {isApiKeyDisabled ? "有効化" : "無効化"}
                  </button>
                </div>
              </div>
            </div>}

            {/* Save Button */}
            <div className="form-actions">
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaveButtonDisabled() || isLoading}
                className="save-btn primary"
              >
                {isLoading ? "保存中..." : "設定を保存する"}
              </button>
            </div>
          </form>
        </div>
      </main>

      {/* Toast Message */}
      {toastMessage && (
        <div className={`toast-message ${toastType}`}>{toastMessage}</div>
      )}

      {/* Delete App CD Confirmation Dialog */}
      <ConfirmationModal
        isOpen={showDeleteAppCdDialog}
        title="app_cdを削除"
        message={`app_cdを再設定すると、マッピング設定がクリアされます。\nこの操作は元に戻せません。`}
        confirmText={isLoading ? "削除中..." : "削除"}
        cancelText="キャンセル"
        confirmButtonColor="danger"
        onConfirm={handleConfirmDeleteAppCd}
        onCancel={handleCancelDeleteAppCd}
      />
    </div>
  );
};

export default SettingsPage;
